import { useState, useEffect, useCallback, useRef } from 'react';
import { docuwarePoCheckApi } from '../services/docuwarePoCheckApi';
import type {
    PoCheckRow, PoCheckSummary, PoCheckFilters, PoCheckSortField,
} from '../types/docuwarePoCheck';
import { EMPTY_PO_CHECK_FILTERS } from '../types/docuwarePoCheck';

/**
 * State for the "Order Check" tab.
 *
 * Same contract as useDocuWareInvoices: sorting, filtering and paging are all server-side, so
 * every setter resets the page - the row that was on page 3 is somewhere else once the order
 * changes.
 *
 * Filters are NOT persisted to sessionStorage, unlike the invoice tab's. This is a work
 * queue: what a reviewer wants on opening it is everything still outstanding, not whatever
 * narrow slice they were looking at on Friday. The default sort puts errors first for the
 * same reason.
 */

export const PO_CHECK_PAGE_SIZE = 50;

const SORT_FIELDS: PoCheckSortField[] = [
    'verdict', 'checked', 'stored', 'supplier', 'invoiceNumber',
    'invoiceDate', 'amount', 'poNo', 'errorCount', 'reviewStatus',
];

/** Dates and counts read better newest/largest first; text and verdicts read better ascending. */
const DESC_BY_DEFAULT = new Set<PoCheckSortField>([
    'checked', 'stored', 'invoiceDate', 'amount', 'errorCount',
]);

const EMPTY_SUMMARY: PoCheckSummary = {
    verdicts: {}, findingCodes: [], findingCategories: [], companies: [], lastCheckedAt: null,
};

export function useDocuWarePoChecks(enabled: boolean) {
    const [rows, setRows] = useState<PoCheckRow[]>([]);
    const [summary, setSummary] = useState<PoCheckSummary>(EMPTY_SUMMARY);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [filters, setFiltersState] = useState<PoCheckFilters>(EMPTY_PO_CHECK_FILTERS);
    const [sortBy, setSortBy] = useState<PoCheckSortField>('verdict');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [page, setPage] = useState(1);

    // Guards against a slow first request landing after a fast second one and overwriting it.
    const requestSeq = useRef(0);

    const load = useCallback(async () => {
        if (!enabled) return;
        const seq = ++requestSeq.current;
        setLoading(true);
        setError(null);
        try {
            const result = await docuwarePoCheckApi.list({
                page, limit: PO_CHECK_PAGE_SIZE, filters, sortBy, sortOrder,
            });
            if (seq !== requestSeq.current) return;
            setRows(result.items);
            setSummary(result.summary);
            setTotal(result.total);
        } catch (err) {
            if (seq !== requestSeq.current) return;
            setError(err instanceof Error ? err.message : 'Failed to load order checks');
            setRows([]);
            setTotal(0);
        } finally {
            if (seq === requestSeq.current) setLoading(false);
        }
    }, [enabled, page, filters, sortBy, sortOrder]);

    useEffect(() => { void load(); }, [load]);

    const setFilters = useCallback((next: Partial<PoCheckFilters>) => {
        setFiltersState(prev => {
            const merged = { ...prev, ...next };
            // Changing the category drops the code: the two compose with AND, so keeping a
            // VAT code while switching to "Quantities" would silently filter to nothing and
            // read as "no findings" rather than as "these two cannot both be true".
            if (next.findingCategory !== undefined && next.findingCategory !== prev.findingCategory) {
                merged.findingCode = '';
            }
            return merged;
        });
        setPage(1);
    }, []);

    const clearFilters = useCallback(() => {
        setFiltersState(EMPTY_PO_CHECK_FILTERS);
        setPage(1);
    }, []);

    /** Click the active column to flip direction; a new column starts at its natural one. */
    const setSort = useCallback((field: PoCheckSortField) => {
        if (!SORT_FIELDS.includes(field)) return;
        setSortBy(prev => {
            if (prev === field) {
                setSortOrder(o => (o === 'asc' ? 'desc' : 'asc'));
                return prev;
            }
            setSortOrder(DESC_BY_DEFAULT.has(field) ? 'desc' : 'asc');
            return field;
        });
        setPage(1);
    }, []);

    const activeFilterCount = Object.values(filters).filter(v => v !== '').length;
    const totalPages = Math.max(1, Math.ceil(total / PO_CHECK_PAGE_SIZE));

    return {
        rows, summary, total, loading, error,
        filters, setFilters, clearFilters, activeFilterCount,
        sortBy, sortOrder, setSort,
        page, setPage, totalPages,
        reload: load,
    };
}
